import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaArrowLeft,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaBoxOpen,
  FaCalendarAlt,
  FaReceipt,
  FaTruck,
  FaChevronRight,
} from "react-icons/fa";
import api from "../api";

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const user = JSON.parse(
    localStorage.getItem("user") || "null"
  );

  const token = user?.access;

  // =========================================================
  // COLORS
  // =========================================================

  const cream = "#F5EFE3";
  const creamLight = "#FBF8F1";
  const teal = "#0F766E";
  const tealDark = "#115E59";
  const brown = "#8B5E3C";
  const border = "#E7DCC9";

  // =========================================================
  // STATUS COLORS
  // =========================================================

  const statusColors = {
    unpaid: "bg-[#F0E8DD] text-[#6B5A4C]",
    paid: "bg-[#E2F0ED] text-[#46736C]",
    pending: "bg-[#F5EEDB] text-[#806B35]",
    processing: "bg-[#E3EEEC] text-[#385F59]",
    shipped: "bg-[#E8E5F0] text-[#5E5870]",
    delivered: "bg-[#E2F0ED] text-[#46736C]",
    completed: "bg-[#E2F0ED] text-[#46736C]",
    cancelled: "bg-red-100 text-red-700",
  };

  // =========================================================
  // FETCH ORDER
  // =========================================================

  useEffect(() => {
    const fetchOrder = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const res = await api.get(
          `/api/orders/${orderId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("Order detail:", res.data);

        setOrder(res.data);
      } catch (err) {
        console.error(
          "Failed to load order:",
          err
        );

        if (err.response) {
          console.error(
            "Status:",
            err.response.status
          );

          console.error(
            "Response:",
            err.response.data
          );
        }

        setOrder(null);
        setError(
          "Unable to load this order."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, token]);

  // =========================================================
  // HELPERS
  // =========================================================

  const formatStatus = (status) => {
    if (!status) {
      return "Unknown";
    }

    return (
      status.charAt(0).toUpperCase() +
      status.slice(1)
    );
  };

  const formatMoney = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "$0.00";
    }

    return `$${number.toFixed(2)}`;
  };

  const formatDate = (value) => {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleString();
  };

  const getItemQuantity = (item) => {
    const quantity = Number(
      item?.quantity || 0
    );

    return Number.isFinite(quantity)
      ? quantity
      : 0;
  };

  const getItemPrice = (item) => {
    const price = Number(
      item?.price || 0
    );

    return Number.isFinite(price)
      ? price
      : 0;
  };

  const getItemSize = (item) => {
    if (!item?.size) {
      return null;
    }

    if (typeof item.size === "string") {
      return item.size;
    }

    if (typeof item.size === "object") {
      if (
        item.size.length_cm !== undefined &&
        item.size.width_cm !== undefined
      ) {
        return `${item.size.length_cm} × ${item.size.width_cm} cm`;
      }

      if (
        item.size.length_ft !== undefined &&
        item.size.width_ft !== undefined
      ) {
        return `${item.size.length_ft} × ${item.size.width_ft} ft`;
      }

      return (
        item.size.name ||
        item.size.label ||
        item.size.size ||
        null
      );
    }

    return null;
  };

  const getItemTotal = (item) => {
    return (
      getItemPrice(item) *
      getItemQuantity(item)
    );
  };

  const getTotalItems = () => {
    if (!Array.isArray(order?.items)) {
      return 0;
    }

    return order.items.reduce(
      (total, item) =>
        total + getItemQuantity(item),
      0
    );
  };

  // =========================================================
  // ORDER STATUS RANK
  // =========================================================

  const statusRank = {
    unpaid: 0,
    pending: 0,
    paid: 1,
    processing: 2,
    shipped: 3,
    delivered: 4,
    completed: 4,
  };

  const timeline = [
    {
      key: "paid",
      title: "Order placed",
    },
    {
      key: "processing",
      title: "Processing",
    },
    {
      key: "shipped",
      title: "Shipped",
    },
    {
      key: "delivered",
      title: "Delivered",
    },
  ];

  const currentStatus =
    order?.status
      ?.toString()
      .toLowerCase() || "pending";

  const currentRank =
    statusRank[currentStatus] ?? 0;

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div
        className="
          min-h-screen
          flex
          items-center
          justify-center

          bg-[#F6F0E6]
        "
      >
        <div className="text-center">
          <div
            className="
              mx-auto
              mb-4

              h-10
              w-10

              rounded-full

              border-4
              border-[#DCCFBE]
              border-t-[#0F766E]

              animate-spin
            "
          />

          <p className="text-[#76695D]">
            Loading order...
          </p>
        </div>
      </div>
    );
  }

  // =========================================================
  // NOT LOGGED IN
  // =========================================================

  if (!token) {
    return (
      <div
        className="
          min-h-screen

          flex
          items-center
          justify-center

          bg-[#F6F0E6]

          px-4
        "
      >
        <div className="text-center">
          <FaBoxOpen
            className="
              mx-auto
              mb-4

              text-5xl

              text-[#B8AA9D]
            "
          />

          <h2
            className="
              text-xl
              sm:text-2xl

              font-semibold

              text-[#332A24]
            "
          >
            Please log in
          </h2>

          <p
            className="
              mt-2

              text-sm
              text-[#76695D]
            "
          >
            Please log in to view this order.
          </p>

          <button
            onClick={() =>
              navigate("/login")
            }
            className="
              mt-6

              rounded-xl

              bg-[#0F766E]

              px-6
              py-3

              text-sm

              font-semibold

              text-white

              transition

              hover:bg-[#115E59]
            "
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  // =========================================================
  // ORDER NOT FOUND
  // =========================================================

  if (!order) {
    return (
      <div
        className="
          min-h-screen

          flex
          flex-col
          justify-center
          items-center

          gap-4

          bg-[#F6F0E6]

          px-4
        "
      >
        <FaBoxOpen
          className="
            text-5xl
            text-[#B8AA9D]
          "
        />

        <h2
          className="
            text-2xl

            font-semibold

            text-[#332A24]
          "
        >
          Order not found
        </h2>

        {error && (
          <p
            className="
              text-sm

              text-[#76695D]

              text-center
            "
          >
            {error}
          </p>
        )}

        <button
          onClick={() =>
            navigate("/orders")
          }
          className="
            inline-flex
            items-center
            gap-2

            bg-[#0F766E]

            text-white

            px-5
            py-3

            rounded-xl

            hover:bg-[#115E59]

            transition
          "
        >
          <FaArrowLeft />

          Back to Orders
        </button>
      </div>
    );
  }

  // =========================================================
  // MAIN PAGE
  // =========================================================

  return (
    <div
      className="
        min-h-screen

        bg-[#F6F0E6]

        py-6
        sm:py-8
        lg:py-12
      "
    >
      <div
        className="
          max-w-6xl

          mx-auto

          px-3
          sm:px-4
          lg:px-6
        "
      >

        {/* =====================================================
            BACK BUTTON
        ====================================================== */}

        <button
          onClick={() =>
            navigate("/orders")
          }
          className="
            mb-5

            inline-flex
            items-center
            gap-2

            text-sm

            text-[#76695D]

            hover:text-[#0F766E]

            transition
          "
        >
          <FaArrowLeft />

          Back to Orders
        </button>

        {/* =====================================================
            ORDER HEADER
        ====================================================== */}

        <div
          className="
            overflow-hidden

            rounded-2xl

            border
            border-[#E7DCC9]

            bg-[#FFFDF9]

            shadow-sm
          "
        >
          <div
            className="
              bg-[#FBF8F1]

              border-b
              border-[#E7DCC9]

              px-4
              sm:px-6
              lg:px-8

              py-5
              sm:py-6
            "
          >
            <div
              className="
                flex

                flex-col
                lg:flex-row

                lg:items-center
                lg:justify-between

                gap-5
              "
            >
              {/* ORDER NUMBER */}

              <div>
                <div
                  className="
                    flex
                    items-center

                    gap-2
                  "
                >
                  <FaReceipt
                    className="
                      text-[#0F766E]
                    "
                  />

                  <p
                    className="
                      text-xs

                      uppercase

                      tracking-[0.1em]

                      font-medium

                      text-[#8A8172]
                    "
                  >
                    Order
                  </p>
                </div>

                <h1
                  className="
                    mt-1

                    text-2xl
                    sm:text-3xl

                    font-bold

                    text-[#332A24]
                  "
                >
                  #{order.order_number}
                </h1>

                {order.created_at && (
                  <div
                    className="
                      mt-2

                      flex
                      items-center
                      gap-2

                      text-xs
                      sm:text-sm

                      text-[#76695D]
                    "
                  >
                    <FaCalendarAlt />

                    <span>
                      Placed on{" "}
                      {formatDate(
                        order.created_at
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* STATUS + TOTAL */}

              <div
                className="
                  flex

                  flex-wrap

                  items-center

                  gap-3
                "
              >
                <span
                  className={`
                    inline-flex

                    items-center

                    gap-2

                    rounded-full

                    px-4
                    py-2

                    text-xs
                    sm:text-sm

                    font-semibold

                    ${
                      statusColors[
                        currentStatus
                      ] ||
                      "bg-[#F0E8DD] text-[#6B5A4C]"
                    }
                  `}
                >
                  <span
                    className="
                      h-2
                      w-2

                      rounded-full

                      bg-current
                    "
                  />

                  {formatStatus(
                    currentStatus
                  )}
                </span>

                <div
                  className="
                    rounded-xl

                    bg-[#E2F0ED]

                    px-4
                    py-2

                    text-right
                  "
                >
                  <p
                    className="
                      text-[10px]

                      text-[#56756F]
                    "
                  >
                    Total
                  </p>

                  <p
                    className="
                      text-lg
                      sm:text-xl

                      font-bold

                      text-[#0F766E]
                    "
                  >
                    {formatMoney(
                      order.total_amount
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* =====================================================
              ORDER STATUS TIMELINE
          ====================================================== */}

          {currentStatus ===
          "cancelled" ? (
            <div className="p-4 sm:p-6">
              <div
                className="
                  rounded-xl

                  border
                  border-red-200

                  bg-red-50

                  px-4
                  py-4
                "
              >
                <p
                  className="
                    font-semibold
                    text-red-700
                  "
                >
                  This order has been cancelled.
                </p>
              </div>
            </div>
          ) : (
            <div
              className="
                px-4
                sm:px-6
                lg:px-8

                py-5
                sm:py-6
              "
            >
              <div
                className="
                  flex
                  items-start
                "
              >
                {timeline.map(
                  (step, index) => {
                    const rank = index + 1;

                    const complete =
                      currentRank >= rank;

                    const last =
                      index ===
                      timeline.length - 1;

                    return (
                      <div
                        key={step.key}
                        className="flex-1 min-w-0"
                      >
                        <div
                          className="
                            flex
                            items-center
                          "
                        >
                          <div
                            className={`
                              h-8
                              w-8
                              sm:h-9
                              sm:w-9

                              shrink-0

                              rounded-full

                              flex
                              items-center
                              justify-center

                              border-2

                              ${
                                complete
                                  ? "bg-[#0F766E] border-[#0F766E] text-white"
                                  : "bg-white border-[#D8CBB7] text-[#B8AA9D]"
                              }
                            `}
                          >
                            {complete ? (
                              <FaCheckCircle className="text-sm" />
                            ) : (
                              <span className="text-[10px]">
                                {rank}
                              </span>
                            )}
                          </div>

                          {!last && (
                            <div
                              className={`
                                h-[2px]

                                flex-1

                                mx-2

                                ${
                                  currentRank >
                                  rank
                                    ? "bg-[#0F766E]"
                                    : "bg-[#E1D7C8]"
                                }
                              `}
                            />
                          )}
                        </div>

                        <p
                          className={`
                            mt-2

                            text-[9px]
                            sm:text-xs

                            font-medium

                            ${
                              complete
                                ? "text-[#115E59]"
                                : "text-[#8A8172]"
                            }
                          `}
                        >
                          {step.title}
                        </p>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )}
        </div>

        {/* =====================================================
            CUSTOMER + SHIPPING
        ====================================================== */}

        <div
          className="
            grid

            grid-cols-1
            lg:grid-cols-2

            gap-5
            lg:gap-6

            mt-5
            lg:mt-6
          "
        >

          {/* ===================================================
              CUSTOMER INFORMATION
          ==================================================== */}

          <section
            className="
              rounded-2xl

              border
              border-[#E7DCC9]

              bg-[#FFFDF9]

              shadow-sm

              p-5
              sm:p-6
            "
          >
            <div
              className="
                flex
                items-center
                gap-3

                pb-4

                border-b
                border-[#E7DCC9]
              "
            >
              <div
                className="
                  flex

                  h-10
                  w-10

                  items-center
                  justify-center

                  rounded-xl

                  bg-[#E2F0ED]

                  text-[#0F766E]
                "
              >
                <FaUser />
              </div>

              <div>
                <h2
                  className="
                    text-base
                    sm:text-lg

                    font-bold

                    text-[#332A24]
                  "
                >
                  Customer Information
                </h2>

                <p
                  className="
                    text-[10px]
                    sm:text-xs

                    text-[#8A8172]
                  "
                >
                  Information associated with this order
                </p>
              </div>
            </div>

            <div
              className="
                mt-5

                space-y-4
              "
            >
              {/* NAME */}

              <div
                className="
                  flex
                  items-start

                  gap-3
                "
              >
                <FaUser
                  className="
                    mt-1

                    shrink-0

                    text-[#0F766E]
                  "
                />

                <div>
                  <p
                    className="
                      text-[10px]
                      sm:text-xs

                      uppercase

                      tracking-wide

                      text-[#9A8E82]
                    "
                  >
                    Full Name
                  </p>

                  <p
                    className="
                      mt-1

                      text-sm
                      sm:text-base

                      font-medium

                      text-[#332A24]
                    "
                  >
                    {order.customer_name ||
                      "Not provided"}
                  </p>
                </div>
              </div>

              {/* EMAIL */}

              <div
                className="
                  flex
                  items-start

                  gap-3
                "
              >
                <FaEnvelope
                  className="
                    mt-1

                    shrink-0

                    text-[#0F766E]
                  "
                />

                <div
                  className="
                    min-w-0
                  "
                >
                  <p
                    className="
                      text-[10px]
                      sm:text-xs

                      uppercase

                      tracking-wide

                      text-[#9A8E82]
                    "
                  >
                    Email
                  </p>

                  <p
                    className="
                      mt-1

                      text-sm
                      sm:text-base

                      font-medium

                      text-[#332A24]

                      break-all
                    "
                  >
                    {order.customer_email ||
                      "Not provided"}
                  </p>
                </div>
              </div>

              {/* PHONE */}

              <div
                className="
                  flex
                  items-start

                  gap-3
                "
              >
                <FaPhone
                  className="
                    mt-1

                    shrink-0

                    text-[#0F766E]
                  "
                />

                <div>
                  <p
                    className="
                      text-[10px]
                      sm:text-xs

                      uppercase

                      tracking-wide

                      text-[#9A8E82]
                    "
                  >
                    Phone
                  </p>

                  <p
                    className="
                      mt-1

                      text-sm
                      sm:text-base

                      font-medium

                      text-[#332A24]
                    "
                  >
                    {order.customer_phone ||
                      "Not provided"}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ===================================================
              SHIPPING INFORMATION
          ==================================================== */}

          <section
            className="
              rounded-2xl

              border
              border-[#E7DCC9]

              bg-[#FFFDF9]

              shadow-sm

              p-5
              sm:p-6
            "
          >
            <div
              className="
                flex
                items-center
                gap-3

                pb-4

                border-b
                border-[#E7DCC9]
              "
            >
              <div
                className="
                  flex

                  h-10
                  w-10

                  items-center
                  justify-center

                  rounded-xl

                  bg-[#E2F0ED]

                  text-[#0F766E]
                "
              >
                <FaTruck />
              </div>

              <div>
                <h2
                  className="
                    text-base
                    sm:text-lg

                    font-bold

                    text-[#332A24]
                  "
                >
                  Shipping Information
                </h2>

                <p
                  className="
                    text-[10px]
                    sm:text-xs

                    text-[#8A8172]
                  "
                >
                  Delivery destination
                </p>
              </div>
            </div>

            <div className="mt-5">
              <div
                className="
                  flex
                  items-start

                  gap-3
                "
              >
                <FaMapMarkerAlt
                  className="
                    mt-1

                    shrink-0

                    text-[#0F766E]
                  "
                />

                <div
                  className="
                    min-w-0
                  "
                >
                  <p
                    className="
                      text-[10px]
                      sm:text-xs

                      uppercase

                      tracking-wide

                      text-[#9A8E82]
                    "
                  >
                    Shipping Address
                  </p>

                  <p
                    className="
                      mt-2

                      text-sm
                      sm:text-base

                      leading-relaxed

                      text-[#332A24]
                    "
                  >
                    {order.shipping_address ||
                      "Not provided"}
                  </p>
                </div>
              </div>

              {/* LOCATION ROW */}

              <div
                className="
                  mt-5

                  grid

                  grid-cols-1
                  sm:grid-cols-2

                  gap-4
                "
              >
                <div>
                  <p
                    className="
                      text-[10px]
                      sm:text-xs

                      uppercase

                      tracking-wide

                      text-[#9A8E82]
                    "
                  >
                    City
                  </p>

                  <p
                    className="
                      mt-1

                      text-sm

                      font-medium

                      text-[#332A24]
                    "
                  >
                    {order.shipping_city ||
                      "Not provided"}
                  </p>
                </div>

                <div>
                  <p
                    className="
                      text-[10px]
                      sm:text-xs

                      uppercase

                      tracking-wide

                      text-[#9A8E82]
                    "
                  >
                    State
                  </p>

                  <p
                    className="
                      mt-1

                      text-sm

                      font-medium

                      text-[#332A24]
                    "
                  >
                    {order.shipping_state ||
                      "Not provided"}
                  </p>
                </div>

                <div>
                  <p
                    className="
                      text-[10px]
                      sm:text-xs

                      uppercase

                      tracking-wide

                      text-[#9A8E82]
                    "
                  >
                    Postal Code
                  </p>

                  <p
                    className="
                      mt-1

                      text-sm

                      font-medium

                      text-[#332A24]
                    "
                  >
                    {order.shipping_postal_code ||
                      "Not provided"}
                  </p>
                </div>

                <div>
                  <p
                    className="
                      text-[10px]
                      sm:text-xs

                      uppercase

                      tracking-wide

                      text-[#9A8E82]
                    "
                  >
                    Country
                  </p>

                  <p
                    className="
                      mt-1

                      text-sm

                      font-medium

                      text-[#332A24]
                    "
                  >
                    {order.shipping_country ||
                      "Not provided"}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* =====================================================
            ORDER ITEMS
        ====================================================== */}

        <section
          className="
            mt-5
            lg:mt-6

            rounded-2xl

            border
            border-[#E7DCC9]

            bg-[#FFFDF9]

            shadow-sm

            overflow-hidden
          "
        >
          {/* HEADER */}

          <div
            className="
              flex
              flex-wrap

              items-center
              justify-between

              gap-3

              px-4
              sm:px-6

              py-4
              sm:py-5

              border-b
              border-[#E7DCC9]

              bg-[#FBF8F1]
            "
          >
            <div>
              <h2
                className="
                  text-lg
                  sm:text-xl
                  md:text-2xl

                  font-bold

                  text-[#332A24]
                "
              >
                Order Items
              </h2>

              <p
                className="
                  mt-1

                  text-xs

                  text-[#8A8172]
                "
              >
                {getTotalItems()}{" "}
                {getTotalItems() === 1
                  ? "item"
                  : "items"}{" "}
                in this order
              </p>
            </div>

            <FaBoxOpen
              className="
                text-xl

                text-[#0F766E]
              "
            />
          </div>

          {/* ITEMS */}

          <div
            className="
              divide-y

              divide-[#E7DCC9]
            "
          >
            {Array.isArray(order.items) &&
            order.items.length > 0 ? (
              order.items.map(
                (item, index) => {
                  const price =
                    getItemPrice(item);

                  const quantity =
                    getItemQuantity(item);

                  const itemTotal =
                    getItemTotal(item);

                  const size =
                    getItemSize(item);

                  return (
                    <div
                      key={
                        item.id ||
                        index
                      }
                      className="
                        p-4
                        sm:p-5
                        md:p-6
                      "
                    >
                      <div
                        className="
                          flex
                          flex-col
                          sm:flex-row

                          sm:items-center

                          gap-4
                        "
                      >

                        {/* PRODUCT IMAGE */}

                        <div
                          className="
                            w-full
                            sm:w-24

                            h-48
                            sm:h-24

                            shrink-0

                            rounded-xl

                            overflow-hidden

                            bg-[#F0E8DD]
                          "
                        >
                          {item.product_image ? (
                            <img
                              src={
                                item.product_image
                              }
                              alt={
                                item.product_name ||
                                "Product"
                              }
                              className="
                                w-full
                                h-full

                                object-cover
                              "
                            />
                          ) : (
                            <div
                              className="
                                w-full
                                h-full

                                flex
                                items-center
                                justify-center

                                text-[#B8AA9D]
                              "
                            >
                              <FaBoxOpen className="text-3xl" />
                            </div>
                          )}
                        </div>

                        {/* PRODUCT INFO */}

                        <div
                          className="
                            flex-1

                            min-w-0
                          "
                        >
                          <h3
                            className="
                              text-base
                              sm:text-lg

                              font-semibold

                              text-[#332A24]
                            "
                          >
                            {item.product_name ||
                              "Product"}
                          </h3>

                          {/* PRODUCT ID */}

                          {item.product_id && (
                            <p
                              className="
                                mt-1

                                text-[10px]
                                sm:text-xs

                                text-[#A39487]
                              "
                            >
                              Product ID:{" "}
                              {item.product_id}
                            </p>
                          )}

                          {/* DETAILS */}

                          <div
                            className="
                              mt-3

                              flex
                              flex-wrap

                              gap-x-5
                              gap-y-2
                            "
                          >
                            <div>
                              <p
                                className="
                                  text-[9px]
                                  sm:text-[10px]

                                  uppercase

                                  tracking-wide

                                  text-[#9A8E82]
                                "
                              >
                                Quantity
                              </p>

                              <p
                                className="
                                  mt-0.5

                                  text-sm

                                  font-medium

                                  text-[#332A24]
                                "
                              >
                                {quantity}
                              </p>
                            </div>

                            <div>
                              <p
                                className="
                                  text-[9px]
                                  sm:text-[10px]

                                  uppercase

                                  tracking-wide

                                  text-[#9A8E82]
                                "
                              >
                                Unit Price
                              </p>

                              <p
                                className="
                                  mt-0.5

                                  text-sm

                                  font-medium

                                  text-[#0F766E]
                                "
                              >
                                {formatMoney(
                                  price
                                )}
                              </p>
                            </div>

                            {size && (
                              <div>
                                <p
                                  className="
                                    text-[9px]
                                    sm:text-[10px]

                                    uppercase

                                    tracking-wide

                                    text-[#9A8E82]
                                  "
                                >
                                  Size
                                </p>

                                <p
                                  className="
                                    mt-0.5

                                    text-sm

                                    font-medium

                                    text-[#332A24]
                                  "
                                >
                                  {size}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* ITEM TOTAL */}

                        <div
                          className="
                            sm:text-right

                            border-t
                            sm:border-t-0

                            border-[#E7DCC9]

                            pt-3
                            sm:pt-0
                          "
                        >
                          <p
                            className="
                              text-[10px]
                              sm:text-xs

                              text-[#8A8172]
                            "
                          >
                            Item total
                          </p>

                          <p
                            className="
                              mt-1

                              text-lg
                              sm:text-xl

                              font-bold

                              text-[#0F766E]
                            "
                          >
                            {formatMoney(
                              itemTotal
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
              )
            ) : (
              <div
                className="
                  p-8

                  text-center

                  text-[#76695D]
                "
              >
                No order items found.
              </div>
            )}
          </div>
        </section>

        {/* =====================================================
            ORDER SUMMARY
        ====================================================== */}

        <section
          className="
            mt-5
            lg:mt-6

            rounded-2xl

            border
            border-[#E7DCC9]

            bg-[#FFFDF9]

            shadow-sm

            p-5
            sm:p-6
            md:p-7
          "
        >
          <div
            className="
              flex
              items-center
              gap-3

              pb-4

              border-b
              border-[#E7DCC9]
            "
          >
            <div
              className="
                flex
                h-10
                w-10

                items-center
                justify-center

                rounded-xl

                bg-[#E2F0ED]

                text-[#0F766E]
              "
            >
              <FaReceipt />
            </div>

            <div>
              <h2
                className="
                  text-lg
                  sm:text-xl

                  font-bold

                  text-[#332A24]
                "
              >
                Order Summary
              </h2>

              <p
                className="
                  text-xs

                  text-[#8A8172]
                "
              >
                Complete order information
              </p>
            </div>
          </div>

          <div
            className="
              mt-5

              flex
              justify-end
            "
          >
            <div
              className="
                w-full
                sm:w-[360px]
              "
            >
              {/* TOTAL */}

              <div
                className="
                  rounded-xl

                  bg-[#F1F8F7]

                  p-4
                "
              >
                <div
                  className="
                    flex
                    items-center
                    justify-between
                  "
                >
                  <span
                    className="
                      text-sm

                      font-medium

                      text-[#5F554D]
                    "
                  >
                    Order Total
                  </span>

                  <span
                    className="
                      text-2xl

                      font-bold

                      text-[#0F766E]
                    "
                  >
                    {formatMoney(
                      order.total_amount
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            FOOTER MESSAGE
        ====================================================== */}

        <div
          className="
            mt-6

            flex
            flex-col
            sm:flex-row

            sm:items-center
            sm:justify-between

            gap-4

            rounded-2xl

            border
            border-[#CFE3DF]

            bg-[#F1F8F7]

            px-5
            sm:px-6

            py-5
          "
        >
          <div
            className="
              flex
              items-start

              gap-3
            "
          >
            <FaCheckCircle
              className="
                mt-0.5

                shrink-0

                text-[#0F766E]
              "
            />

            <div>
              <p
                className="
                  text-sm

                  font-semibold

                  text-[#115E59]
                "
              >
                Thank you for shopping with A1 Rugs!
              </p>

              <p
                className="
                  mt-1

                  text-xs

                  text-[#56756F]
                "
              >
                Keep your order number for future reference.
              </p>
            </div>
          </div>

          <button
            onClick={() =>
              navigate("/orders")
            }
            className="
              inline-flex

              items-center
              justify-center
              gap-2

              rounded-xl

              bg-[#0F766E]

              px-5
              py-2.5

              text-xs
              sm:text-sm

              font-semibold

              text-white

              transition

              hover:bg-[#115E59]
              hover:-translate-y-0.5
            "
          >
            All Orders

            <FaChevronRight className="text-[10px]" />
          </button>
        </div>
      </div>
    </div>
  );
}